import { useState } from "react";
import { Button, TextInput, useToast } from "@a2z/ui";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { useUpdateStoreSettingsMutation } from "@/modules/settings/services/useUpdateStoreSettingsMutation";
import { Card, Label, Toggle } from "./primitives";

type InventoryPanelProps = {
  storeSlug: string;
  lowStockThreshold: number;
  allowOverselling: boolean;
  unlimitedStock: boolean;
};

/**
 * Store-level inventory defaults: the low-stock badge threshold (applies to
 * every product that doesn't override it), whether the store allows
 * overselling (accepting orders beyond available stock — stock can go
 * negative), and whether stock is unlimited (never out of stock, counts
 * frozen). All are overridable per product on the product edit page.
 */
export function InventoryPanel({
  storeSlug,
  lowStockThreshold,
  allowOverselling,
  unlimitedStock,
}: InventoryPanelProps) {
  const t = useTranslations();
  const ss = keys.seller.storeSettings;
  const toast = useToast();
  const { mutateAsync: updateSettings, isPending } =
    useUpdateStoreSettingsMutation();

  const [threshold, setThreshold] = useState<string>(String(lowStockThreshold));
  const [oversell, setOversell] = useState<boolean>(allowOverselling);
  const [unlimited, setUnlimited] = useState<boolean>(unlimitedStock);

  const parsedThreshold = Number.parseInt(threshold, 10);
  const thresholdValid =
    Number.isFinite(parsedThreshold) && parsedThreshold >= 0;
  const dirty =
    !thresholdValid ||
    parsedThreshold !== lowStockThreshold ||
    oversell !== allowOverselling ||
    unlimited !== unlimitedStock;

  const handleSave = async (): Promise<void> => {
    if (!thresholdValid || isPending) return;
    try {
      await updateSettings({
        storeSlug,
        lowStockThreshold: parsedThreshold,
        allowOverselling: oversell,
        unlimitedStock: unlimited,
      });
      toast.success(t(ss.inventorySaved));
    } catch {
      toast.error(t(keys.common.error));
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <Card title={t(ss.inventoryLowStockLabel)} desc={t(ss.inventoryLowStockDesc)}>
        <div className="flex flex-col gap-1.5" style={{ maxWidth: 240 }}>
          <Label>{t(ss.inventoryLowStockField)}</Label>
          <TextInput
            name="lowStockThreshold"
            type="number"
            min={0}
            step={1}
            value={threshold}
            onChange={(e) => setThreshold(e.target.value)}
            aria-label={t(ss.inventoryLowStockField)}
          />
          {!thresholdValid ? (
            <span className="text-[12px] text-[color:var(--error-500)]">
              {t(ss.inventoryLowStockInvalid)}
            </span>
          ) : null}
        </div>
      </Card>

      <Card
        title={t(ss.inventoryOversellLabel)}
        desc={t(ss.inventoryOversellDesc)}
        action={
          <Toggle on={oversell} onChange={(next) => setOversell(next)} />
        }
      >
        <span className="text-[12.5px] text-[color:var(--text-muted)]">
          {oversell
            ? t(ss.inventoryOversellOn)
            : t(ss.inventoryOversellOff)}
        </span>
      </Card>

      <Card
        title={t(ss.inventoryUnlimitedLabel)}
        desc={t(ss.inventoryUnlimitedDesc)}
        action={
          <Toggle on={unlimited} onChange={(next) => setUnlimited(next)} />
        }
      >
        <span className="text-[12.5px] text-[color:var(--text-muted)]">
          {unlimited
            ? t(ss.inventoryUnlimitedOn)
            : t(ss.inventoryUnlimitedOff)}
        </span>
      </Card>

      <div>
        <Button
          variant="secondary"
          size="md"
          onClick={() => void handleSave()}
          disabled={!dirty || !thresholdValid || isPending}
        >
          {t(keys.common.save)}
        </Button>
      </div>
    </div>
  );
}
